// src/components/resources/templates/TemplatesContent.tsx

'use client'

import { Suspense } from 'react'
import Link from 'next/link'
import type { Template } from '~/lib/resources/types/templates'
import ResourceGrid from '~/components/resources/ResourceGrid'
import { ResourceGridSkeleton } from '~/components/resources/ResourceGridSkeleton'
import { ArrowRightIcon } from '@heroicons/react/24/outline'

interface TemplatesContentProps {
  templates: Template[]
}

// Categories component — non-interactive info cards.
//
// These cards summarize the kinds of templates available in the catalog
// below but do NOT filter or navigate. Filtering isn't wired server-side,
// so the prior `?category=` hrefs produced duplicate "Crawled - currently
// not indexed" URLs in Search Console. The intermediate fix (linking all
// three to `#templates`) addressed the SEO duplication but left a false
// affordance: three visually identical "clickable" cards that all jump
// to the same anchor below. Dropping the link removes the affordance.
//
// If real filtering lands later, switch these back to `<Link>` and wire
// the searchParams reading in this component.
//
// Styling note: these panels deliberately avoid the site's link-card idiom
// (bordered rounded cards with hover states) — a flat tinted background and
// no hover treatment so they read as informational labels, not dead links.
function CategoriesGrid(): JSX.Element {
  const categories = [
    {
      title: 'Campaign Planning',
      description: 'Strategic templates for planning, budgeting, and organizing marketing campaigns across channels',
      icon: '📋',
      features: ['Campaign briefs', 'Budget planners', 'Timeline templates'],
      color: 'from-blue-500/10 to-blue-600/10 dark:from-blue-400/10 dark:to-blue-500/10'
    },
    {
      title: 'Creative Strategy',
      description: 'Templates to develop and document creative strategies, brand guidelines, and content plans',
      icon: '🎨',
      features: ['Brand guidelines', 'Content calendars', 'Creative briefs'],
      color: 'from-purple-500/10 to-purple-600/10 dark:from-purple-400/10 dark:to-purple-500/10'
    },
    {
      title: 'Performance Tracking',
      description: 'Analytics and reporting templates to track, measure, and optimize campaign performance',
      icon: '📊',
      features: ['Performance dashboards', 'ROI calculators', 'A/B test trackers'],
      color: 'from-green-500/10 to-green-600/10 dark:from-green-400/10 dark:to-green-500/10'
    }
  ]

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {categories.map((category) => (
        <div
          key={category.title}
          className="relative rounded-2xl bg-gray-50 p-6 dark:bg-gray-900/40"
        >
          <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${category.color} opacity-50`} />
          <div className="relative">
            <div className="text-3xl mb-4">{category.icon}</div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {category.title}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {category.description}
            </p>
            <ul className="mt-4 space-y-2">
              {category.features.map((feature): JSX.Element => (
                <li key={feature} className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <span className="mr-2 h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  )
}

export function TemplatesContent({ templates }: TemplatesContentProps): JSX.Element {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      {/* Enhanced Hero Section.

          `isolate` is load-bearing: the decorative -z-10 gradient blob below
          needs a PERMANENT stacking context on this section. Without it, the
          blob's only stacking context was the layout's mount fade
          (ClientTemplatesLayout's motion.div) — while that opacity animation
          ran the blob painted above the page background (visible color), and
          the moment it finished (opacity: 1 drops the stacking context) the
          negative-z blob fell BEHIND the opaque body background and vanished.
          That was the "flash of color behind the hero text that then goes
          away" (#561 `c4920797286`). With the section isolated, the glow
          renders consistently — during and after the mount animation. */}
      <section className="relative isolate py-12 sm:py-16">
        <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-blue-500 to-purple-500 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)'
            }}
          />
        </div>

        <div className="relative">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl lg:text-6xl">
            Marketing Templates
            <span className="block text-blue-600 dark:text-blue-400">
              Built for Growth
            </span>
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-gray-600 dark:text-gray-400">
            Professional, customizable templates to streamline your marketing workflow. From campaign planning to performance tracking, we&apos;ve got you covered.
          </p>
          <div className="mt-8 flex gap-4">
            <Link
              href="#templates"
              className="inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:bg-blue-500 dark:hover:bg-blue-400"
            >
              Browse Templates
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Link>
            <Link
              href="#categories"
              className="inline-flex items-center rounded-lg bg-gray-50 px-6 py-3 text-sm font-semibold text-gray-900 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              View Categories
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Grid with enhanced visual design */}
      <section id="categories" className="py-16">
        <div className="mb-12 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            Template Categories
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            Choose from our curated collection of marketing templates
          </p>
        </div>
        <CategoriesGrid />
      </section>

      {/* Featured Templates Section */}
      <section className="py-16">
        <div className="mb-12">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Popular Templates
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            Our most used and highest-rated marketing templates
          </p>
        </div>
        <Suspense fallback={<ResourceGridSkeleton type="featured" count={3} />}>
          <ResourceGrid 
            resources={templates.filter(t => t.featured).slice(0, 3)}
            type="featured"
            category="template"
          />
        </Suspense>
      </section>

      {/* All Templates Grid */}
      <section id="templates" className="py-16">
        <div className="mb-12">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            All Templates
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            Browse our complete collection of marketing templates
          </p>
        </div>
        <Suspense fallback={<ResourceGridSkeleton type="default" count={6} />}>
          <ResourceGrid 
            resources={templates}
            type="default"
            category="template"
          />
        </Suspense>
      </section>
    </div>
  )
} 